import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { X, Fingerprint, Shield, Github, Chrome, Apple, ArrowRight, Key, AlertCircle, Loader2, Lock } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
}

type AuthMethod = 'password' | 'passphrase'

// Regex patterns for input detection
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// ΛiD formats: Λusername, @username, or plain username (3-32 chars, alphanumeric + underscore/hyphen)
const LAMBDA_ID_PATTERN = /^[Λ@]?[a-zA-Z][a-zA-Z0-9_-]{2,31}$/

/**
 * Login Authentication Modal - Minimal Design
 *
 * Features:
 * - Email or ΛiD input with auto-detection
 * - Password or Passphrase authentication
 * - Passkey/biometric quick sign-in
 * - OAuth providers
 */
export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [identifier, setIdentifier] = useState('')
  const [secret, setSecret] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password')

  const navigate = useNavigate()
  const { login, loginWithPasskey, loginWithOAuth, isLoading, error, clearError, isAuthenticated } = useAuthStore()

  // Detect input type (email vs ΛiD)
  const inputType = useMemo(() => {
    if (!identifier) return null
    const trimmed = identifier.trim()
    // Check email first (contains @ with domain)
    if (EMAIL_PATTERN.test(trimmed)) return 'email'
    // Check for ΛiD patterns: Λ prefix, @ prefix, or plain username
    if (trimmed.startsWith('Λ') || trimmed.startsWith('@')) return 'lambdaId'
    // If it looks like a username (no @ in middle, alphanumeric), treat as ΛiD
    if (LAMBDA_ID_PATTERN.test(trimmed)) return 'lambdaId'
    return 'unknown'
  }, [identifier])

  // Close modal when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      onClose()
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate, onClose])

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearError()
      setIdentifier('')
      setSecret('')
      setAuthMethod('password')
    }
  }, [isOpen, clearError])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Determine if using email or ΛiD
      const isEmail = inputType === 'email'
      await login({
        email: isEmail ? identifier : undefined,
        lambdaId: !isEmail ? identifier : undefined,
        password: authMethod === 'password' ? secret : undefined,
        passphrase: authMethod === 'passphrase' ? secret : undefined,
        rememberMe
      })
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  const handlePasskeyLogin = async () => {
    try {
      await loginWithPasskey()
    } catch (err) {
      console.error('Passkey login failed:', err)
    }
  }

  const handleOAuthLogin = (provider: 'google' | 'github' | 'apple') => {
    loginWithOAuth(provider)
  }

  const goToOnboarding = () => {
    onClose()
    navigate('/onboarding')
  }

  // Helvetica Neue Ultra Light font style
  const brandFontStyle: React.CSSProperties = {
    fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", "Helvetica", Arial, sans-serif',
    fontWeight: 100,
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal - Horizontal Layout */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-4xl"
        >
          <div className="bg-black border border-white/20 overflow-hidden">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 text-white/60 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Two Column Layout */}
            <div className="flex flex-col md:flex-row">
              {/* Left Column - Branding & Quick Sign In */}
              <div className="md:w-2/5 p-8 border-b md:border-b-0 md:border-r border-white/10">
                {/* Header */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-1" style={brandFontStyle}>
                    <span className="text-3xl text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}>
                      Λ
                    </span>
                    <span className="text-xl text-white" style={{ textShadow: '0 0 15px rgba(255,255,255,0.4)' }}>
                      iD
                    </span>
                  </div>
                  <p className="text-white/70 text-sm font-light mt-2">
                    Sign in to your identity
                  </p>
                </div>

                {/* Quick Sign In */}
                <div className="mb-8">
                  <p className="text-[10px] text-white/50 uppercase tracking-widest mb-3">
                    Quick sign in
                  </p>

                  {/* Passkey */}
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}
                    className="w-full py-3 mb-3 border border-white/20 hover:border-white/50 hover:bg-white/5
                             disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                             flex items-center justify-center gap-2 text-white/80 hover:text-white text-sm font-light"
                  >
                    <Fingerprint className="w-4 h-4" />
                    <span>Passkey</span>
                  </button>

                  {/* OAuth Providers */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleOAuthLogin('google')}
                      disabled={isLoading}
                      className="flex-1 p-3 border border-white/20 hover:border-white/50 hover:bg-white/5
                               disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                               text-white/80 hover:text-white flex items-center justify-center"
                      aria-label="Sign in with Google"
                    >
                      <Chrome className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOAuthLogin('apple')}
                      disabled={isLoading}
                      className="flex-1 p-3 border border-white/20 hover:border-white/50 hover:bg-white/5
                               disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                               text-white/80 hover:text-white flex items-center justify-center"
                      aria-label="Sign in with Apple"
                    >
                      <Apple className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOAuthLogin('github')}
                      disabled={isLoading}
                      className="flex-1 p-3 border border-white/20 hover:border-white/50 hover:bg-white/5
                               disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                               text-white/80 hover:text-white flex items-center justify-center"
                      aria-label="Sign in with GitHub"
                    >
                      <Github className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Create Account */}
                <div className="pt-6 border-t border-white/10">
                  <p className="text-white/60 text-xs mb-3 font-light">
                    New to LUKHAS?
                  </p>
                  <button
                    type="button"
                    onClick={goToOnboarding}
                    className="w-full py-3 border border-white/30 text-white/80 hover:text-white hover:border-white/50
                             transition-all text-sm font-light flex items-center justify-center gap-2"
                  >
                    Create Your ΛiD
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Right Column - Login Form */}
              <div className="md:w-3/5 p-8">
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-3 border border-white/20 bg-white/5"
                    role="alert"
                    aria-live="assertive"
                  >
                    <p className="text-white text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </p>
                  </motion.div>
                )}

                {/* Login Form */}
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  {/* Email or ΛiD Input */}
                  <div>
                    <label htmlFor="identifier" className="block text-xs text-white/70 mb-2 uppercase tracking-wider font-light">
                      Email or ΛiD#
                    </label>
                    <div className="relative">
                      {/* ΛiD# prefix */}
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-sm font-light pointer-events-none">
                        ΛiD#
                      </span>
                      <input
                        id="identifier"
                        type="text"
                        value={identifier}
                        onChange={(e) => {
                          setIdentifier(e.target.value)
                          if (error) clearError()
                        }}
                        required
                        disabled={isLoading}
                        autoFocus
                        autoComplete="username"
                        className="w-full pl-14 pr-16 py-3
                                 bg-black border border-white/20
                                 text-white placeholder-white/40
                                 focus:outline-none focus:border-white/50
                                 disabled:opacity-50 transition-all text-sm font-light"
                        placeholder="username or email"
                      />
                      {/* Input type indicator */}
                      {identifier && (
                        <div
                          className="absolute right-4 top-1/2 -translate-y-1/2"
                          aria-live="polite"
                        >
                          {inputType === 'email' && (
                            <span className="text-[10px] text-white/50 uppercase tracking-wider">Email</span>
                          )}
                          {inputType === 'lambdaId' && (
                            <span className="text-[10px] text-white/70 uppercase tracking-wider">ΛiD</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Auth Method Toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAuthMethod('password')}
                      aria-pressed={authMethod === 'password'}
                      className={`flex-1 py-2 text-xs uppercase tracking-wider font-light transition-all
                                ${authMethod === 'password'
                                  ? 'bg-white/10 text-white border border-white/30'
                                  : 'text-white/60 border border-white/10 hover:text-white/80'
                                }`}
                    >
                      <Key className="w-3 h-3 inline mr-1.5" />
                      Password
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuthMethod('passphrase')}
                      aria-pressed={authMethod === 'passphrase'}
                      className={`flex-1 py-2 text-xs uppercase tracking-wider font-light transition-all
                                ${authMethod === 'passphrase'
                                  ? 'bg-white/10 text-white border border-white/30'
                                  : 'text-white/60 border border-white/10 hover:text-white/80'
                                }`}
                    >
                      <Lock className="w-3 h-3 inline mr-1.5" />
                      Passphrase
                    </button>
                  </div>

                  {/* Password/Passphrase Input */}
                  <div>
                    <label htmlFor="secret" className="block text-xs text-white/70 mb-2 uppercase tracking-wider font-light">
                      {authMethod === 'password' ? 'Password' : 'Passphrase'}
                    </label>
                    <input
                      id="secret"
                      type="password"
                      value={secret}
                      onChange={(e) => {
                        setSecret(e.target.value)
                        if (error) clearError()
                      }}
                      required
                      disabled={isLoading}
                      autoComplete={authMethod === 'password' ? 'current-password' : 'off'}
                      className="w-full px-4 py-3
                               bg-black border border-white/20
                               text-white placeholder-white/40
                               focus:outline-none focus:border-white/50
                               disabled:opacity-50 transition-all text-sm font-light"
                      placeholder={authMethod === 'password' ? 'Enter your password' : 'Enter your recovery passphrase'}
                    />
                    {authMethod === 'passphrase' && (
                      <p className="text-[10px] text-white/50 mt-2 font-light">
                        Your 12 or 24 word recovery passphrase
                      </p>
                    )}
                  </div>

                  {/* Remember Me & Forgot */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                        className="w-3 h-3 border-white/30 bg-black
                                 text-white focus:ring-white/50 focus:ring-offset-0 focus:ring-1"
                      />
                      <span className="text-xs text-white/70 font-light">Remember me</span>
                    </label>
                    <a href="/password-reset" className="text-xs text-white/70 hover:text-white transition-colors font-light">
                      Forgot password?
                    </a>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !identifier || !secret}
                    className="w-full py-4 bg-white text-black text-sm font-medium tracking-wide
                             disabled:opacity-30 disabled:cursor-not-allowed
                             hover:bg-white/90 transition-all
                             flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <span>Sign In</span>
                    )}
                  </button>
                </form>

                {/* Security Note */}
                <div className="mt-6 flex items-center justify-center gap-2 text-white/50">
                  <Shield className="w-3 h-3" />
                  <span className="text-[10px] uppercase tracking-widest font-light">End-to-end encrypted</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
